/**
 * Shared Utilities
 *
 * Common functions used across widget and full-screen chat.
 */

/**
 * Detect if device is mobile
 */
export function isMobile() {
  const userAgentMatch = /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(navigator.userAgent);
  const widthMatch = (window.matchMedia && window.matchMedia('(max-width: 768px)').matches);
  return userAgentMatch || widthMatch;
}

/**
 * Detect if device is iOS
 */
export function isIOS() {
  return /iPad|iPhone|iPod/.test(navigator.userAgent) && !window.MSStream;
}

/**
 * Format timestamp for messages
 */
export function formatTimestamp(date = new Date()) {
  return date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
}

/**
 * Debounce function for performance
 */
export function debounce(func, wait) {
  let timeout;
  return function executedFunction(...args) {
    const later = () => {
      clearTimeout(timeout);
      func(...args);
    };
    clearTimeout(timeout);
    timeout = setTimeout(later, wait);
  };
}

/**
 * Scroll element to bottom smoothly
 */
export function scrollToBottom(element) {
  if (!element) return;
  requestAnimationFrame(() => {
    element.scrollTop = element.scrollHeight;
  });
}

/**
 * Generate unique ID for messages/sessions
 */
export function generateId() {
  return `${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;
}

/**
 * Parse query parameters from URL
 */
export function getQueryParams() {
  const params = new URLSearchParams(window.location.search);
  const result = {};
  for (const [key, value] of params) {
    result[key] = value;
  }
  return result;
}

/**
 * Safe JSON parse with fallback
 */
export function safeJSONParse(str, fallback = null) {
  try {
    return JSON.parse(str);
  } catch (e) {
    console.warn('[Utils] JSON parse failed:', e);
    return fallback;
  }
}

/**
 * Safe localStorage getItem with fallback
 */
export function getStorageItem(key, fallback = null) {
  try {
    const item = localStorage.getItem(key);
    return item !== null ? item : fallback;
  } catch (e) {
    console.warn('[Utils] localStorage access failed:', e);
    return fallback;
  }
}

/**
 * Safe localStorage setItem
 */
export function setStorageItem(key, value) {
  try {
    localStorage.setItem(key, value);
    return true;
  } catch (e) {
    console.warn('[Utils] localStorage set failed:', e);
    return false;
  }
}

/**
 * Remove storage item
 */
export function removeStorageItem(key) {
  try {
    localStorage.removeItem(key);
    return true;
  } catch (e) {
    console.warn('[Utils] localStorage remove failed:', e);
    return false;
  }
}
